import { useRef } from 'react';

import PropTypes from 'prop-types';
import { BsArrowUp, BsStopFill } from 'react-icons/bs';

import LimitChips from './LimitChips';

const MAX_HEIGHT_PX = 160;

/**
 * The text field with the limit chips below and the send (or stop) button.
 */
const Composer = ({
	value,
	onChange,
	onSend,
	onStop,
	placeholder,
	busy,
	working,
	chipsLocked,
	mandate,
	onMandateChange,
}) => {
	const textareaRef = useRef(null);

	const grow = (el) => {
		el.style.height = 'auto';
		el.style.height = `${Math.min(MAX_HEIGHT_PX, el.scrollHeight)}px`;
	};

	const submit = () => {
		if (!value.trim() || busy) return;
		onSend(value.trim());
		if (textareaRef.current) textareaRef.current.style.height = 'auto';
	};

	return (
		<div className="mx-auto w-full max-w-[720px]">
			<div className="rounded-2xl border border-bgBase-500 bg-white shadow-field transition-shadow focus-within:border-primary-300 focus-within:shadow-field-focus">
				<textarea
					ref={textareaRef}
					value={value}
					rows={1}
					onChange={(e) => {
						onChange(e.target.value);
						grow(e.target);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && !e.shiftKey) {
							e.preventDefault();
							submit();
						}
					}}
					placeholder={placeholder}
					aria-label="Nachricht an den Assistenten"
					className="w-full resize-none bg-transparent px-4 pb-2 pt-3.5 text-md leading-6 text-text placeholder:text-bgBase-700 focus:outline-none"
				/>
				<div className="flex items-end justify-between gap-3 px-3 pb-3">
					<LimitChips value={mandate} onChange={onMandateChange} disabled={working || chipsLocked} />
					{working ? (
						<button
							type="button"
							onClick={onStop}
							title="Stoppen"
							className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-text text-white hover:bg-black"
						>
							<BsStopFill className="h-4 w-4" />
						</button>
					) : (
						<button
							type="button"
							onClick={submit}
							disabled={!value.trim() || busy}
							title="Senden"
							className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-primary-500 text-white transition-colors hover:bg-primary-600 disabled:bg-bgBase-500 disabled:text-bgBase-700"
						>
							<BsArrowUp className="h-4 w-4" />
						</button>
					)}
				</div>
			</div>
			<p className="mt-2 text-center text-xs text-bgBase-800">
				Kostenpflichtige Schritte startet der Assistent erst nach deiner Bestätigung.
			</p>
		</div>
	);
};

Composer.propTypes = {
	value: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
	onSend: PropTypes.func.isRequired,
	onStop: PropTypes.func.isRequired,
	placeholder: PropTypes.string.isRequired,
	busy: PropTypes.bool,
	// The draft is being created with the chips as its mandate: a change now would be lost.
	chipsLocked: PropTypes.bool,
	working: PropTypes.bool,
	mandate: PropTypes.object.isRequired,
	onMandateChange: PropTypes.func.isRequired,
};

export default Composer;
